<template>
  <div class="client opportunity locations-and-dates-editor">
    <v-btn icon @click="editing = !editing"> <v-icon>mdi-pencil</v-icon> </v-btn>
    <v-dialog v-model="editing" max-width="900px" width="66vw">
      <v-card>
        <v-card-title class="title"> Locations and Dates </v-card-title>

        <v-divider />

        <v-card-text>
          <v-form v-model="valid" ref="form">
            <v-combobox
              chips
              hide-selected
              multiple
              return-object
              label="Opportunity Locations"
              v-model="modifiedOpportunity.locations"
              :items="missingLocations"
              :color="themes.client.light.input"
              :search-input.sync="search"
              :filter="filter"
              :loading="loading"
              item-text="address"
              @change="modifyLocations"
              ref="locationsInput"
            >
              <template slot="no-data">
                <v-list-tile v-if="search && search.length">
                  <span class="subheading">Add</span>
                  <v-chip>
                    <v-icon left>mdi-map-marker</v-icon>
                    {{ search }}
                  </v-chip>
                </v-list-tile>
                <v-list-tile v-else>
                  <span class="subheading">Provide an address</span>
                </v-list-tile>
              </template>

              <template
                v-if="item === Object(item)"
                slot="selection"
                slot-scope="{
                  item,
                  parent,
                  selected,
                }"
              >
                <v-chip
                  :selected="selected"
                  :class="themes.client.light.chip"
                  :key="`location-selection-${item.id}`"
                >
                  <v-avatar>
                    <img
                      :class="
                        `flag-icon-background flag-icon-squared flag-icon-${item.countryCode.toLowerCase()}`
                      "
                    />
                  </v-avatar>
                  {{ item.address }}
                  <v-icon right small @click="remove({ id: item.id })">mdi-close</v-icon>
                </v-chip>
              </template>

              <template slot="item" slot-scope="{ index, item, parent }">
                <v-chip :class="themes.client.light.chip" :key="`location-item-${item.id}`">
                  <v-avatar>
                    <img
                      :class="
                        `flag-icon-background flag-icon-squared flag-icon-${item.countryCode.toLowerCase()}`
                      "
                    />
                  </v-avatar>
                  {{ item.address }}
                </v-chip>
              </template>
            </v-combobox>

            <template v-for="location in modifiedOpportunity.locations">
              <v-text-field
                :key="`intake-input-${location.id}`"
                :label="`Intake at ${location.address}`"
                v-model="location.intake"
                type="number"
                :color="themes.client.light.input"
                :min="0"
                :rules="rules.intake"
                :messages="location.intake === 0 ? ['Setting the intake to zero removes the location from the application options'] : []"
                prepend-icon="mdi-account-multiple-plus"
              />
            </template>

            <date-picker
              label="Opening Date"
              v-model="modifiedOpportunity.opensAt"
              :rules="rules.opensAt"
              required
              :error-messages="errors.opens_at"
              :color="themes.client.light.input"
              prepend-icon="mdi-calendar-clock"
            />

            <date-picker
              label="Closing Date"
              v-model="modifiedOpportunity.closesAt"
              :rules="rules.closesAt"
              required
              :error-messages="errors.closes_at"
              :color="themes.client.light.input"
              prepend-icon="mdi-calendar-remove"
            />

            <date-picker
              clearable
              label="Commencement Date"
              v-model="modifiedOpportunity.commencesAt"
              :rules="rules.commencesAt"
              append-icon="mdi-calendar"
              :color="themes.client.light.input"
              prepend-icon="mdi-calendar-check"
            />
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn flat @click="editing = false" :color="themes.client.light.cancel"> Cancel </v-btn>
          <v-btn @click="submit" :color="themes.client.light.primaryActionButton"> Save </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Helpers
import _ from 'lodash'

// Vue Component
export default {
  props: {
    opportunity: Object,
    organization: Object,
    edit: Boolean,
  },

  data() {
    return {
      modifiedOpportunity: JSON.parse(JSON.stringify(this.opportunity)),
      search: '',
      editing: false,
      loading: false,
      valid: true,
      rules: {
        intake: [v => v >= 0 || 'must be a positive number or 0 to disable the location'],
        opensAt: [v => !!v || 'must be specified'],
        closesAt: [v => !!v || 'must be specified'],
        commencesAt: [],
      },
      errors: {
        opens_at: [],
        closes_at: [],
      },
    }
  },

  computed: {
    missingLocations() {
      if (this.organization.locations && this.opportunity.locations) {
        return this.organization.locations.filter(location => {
          return !this.opportunity.locations.map(l => l.locationId).includes(location.id)
        })
      }

      return this.organization.locations ? this.organization.locations : null
    },
  },

  watch: {
    editing() {
      this.modifiedOpportunity = JSON.parse(JSON.stringify(this.opportunity))
    },
  },

  methods: {
    filter(item, queryText, itemText) {
      const hasValue = val => (val != null ? val : '')

      const text = hasValue(itemText)
      const query = hasValue(queryText)

      return (
        text
          .toString()
          .toLowerCase()
          .indexOf(query.toString().toLowerCase()) > -1
      )
    },

    modifyLocations(locations) {
      // Create any that were newly added
      let newValue = locations.filter(location => {
        return (
          !location.id ||
          (location.id && this.opportunity.locations.every(l => l.id !== location.id))
        )
      })
      if (newValue && typeof newValue[0] === 'string') newValue = [{ address: newValue[0] }]
      if (newValue && newValue[0]) this.create(newValue[0])

      // Remove any that were removed with backspace
      const removedValues = this.opportunity.locations.filter(l => {
        return !_.some(locations, location => location.id === l.id)
      })
      if (removedValues && removedValues[0]) this.remove(removedValues[0])
    },

    create(location) {
      if (location && location.address) {
        this.loading = true

        this.$store
          .dispatch('client/opportunities/createLocation', {
            opportunity: {
              id: this.opportunity.id,
            },
            ...location,
          })
          .then(errors => {
            if (errors) {
              this.errors = errors
              this.$store.commit('snackbar', {
                text: 'There was a problem adding the new address',
                type: 'error',
              })
            } else {
              this.$store.commit('snackbar', {
                text: 'The address was added successfully',
                type: 'success',
              })
              this.refresh()
            }
          })
      }
    },

    remove(address) {
      if (address && address.id) {
        this.loading = true

        this.$store
          .dispatch('client/opportunities/removeLocation', {
            opportunity: {
              id: this.opportunity.id,
            },
            id: address.id,
          })
          .then(errors => {
            if (errors) {
              this.$store.commit('snackbar', {
                text: 'There was a problem removing the address',
                type: 'error',
              })
            } else {
              this.$store.commit('snackbar', {
                text: 'The address was removed successfully',
                type: 'success',
              })
              this.refresh()
            }
          })
      }
    },

    submit() {
      if (this.$refs.form.validate()) {


        // Update opportunity dates
        this.$store
          .dispatch('client/opportunities/update', this.modifiedOpportunity)
          .then(errors => {
            if (errors) {
              this.errors = errors
              this.$store.dispatch('snackbarError', 'There was a problem updating the dates')
            } else {
              this.editing = false
              this.$store.dispatch('snackbarSuccess', 'Dates were updated successfully')

              // Update locations where intake changed
              this.modifiedOpportunity.locations.forEach(location => {
                const hasChanged = this.opportunity.locations.some(l => {
                  return l.id === location.id && l.intake !== location.intake
                })
                if (hasChanged) {
                  this.$store.dispatch('client/opportunities/updateLocation', {
                    opportunity: {
                      id: this.opportunity.id,
                    },
                    ...location,
                  })
                }
              })
            }
          })
      }
    },

    refresh() {
      this.$refs.locationsInput.blur()
      this.$store.dispatch('client/opportunities/fetch', this.$route.params.id).then(() => {
        this.modifiedOpportunity = JSON.parse(JSON.stringify(this.opportunity))
        this.loading = false
      })
    },
  },
}
</script>
